/**
 * "You changed a key and your app hasn't picked it up yet", remembered
 * across page loads.
 *
 * The reminder used to live in the Keys screen's own component state, so it
 * appeared once and then vanished the moment somebody navigated away, which
 * is precisely backwards: the fact it reports is not "you just did a thing",
 * it is "your live app is running with an older set of keys than the one on
 * this page", and that stays true until a deploy picks them up. So it is
 * stored per project and cleared by the only thing that can honestly clear
 * it, a deploy that started after the change.
 *
 * localStorage rather than the control plane: the platform sets the secret
 * on the app the moment it is saved and keeps no "pending" flag of its own,
 * so there is nothing server-side to read this off. Per browser is the
 * honest scope for it, and a reminder that fails to appear on a second
 * machine is a smaller wrong than one that never appears at all. Every
 * access is wrapped, because a private window or a browser refusing site
 * data throws on the accessor itself.
 */

const KEY_PREFIX = 'sv-cloud-keys-changed:';

function storageKey(projectId: string): string {
  return `${KEY_PREFIX}${projectId}`;
}

/** When the keys were last changed on this project, in epoch ms. */
export function readKeysChangedAt(projectId: string): number | null {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const at = Number(raw);
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}

export function markKeysChanged(projectId: string, at: number = Date.now()): void {
  if (!projectId) return;
  try {
    localStorage.setItem(storageKey(projectId), String(at));
  } catch {
    /* See the file header: storage is best effort here. */
  }
}

export function clearKeysChanged(projectId: string): void {
  if (!projectId) return;
  try {
    localStorage.removeItem(storageKey(projectId));
  } catch {
    /* See the file header. */
  }
}

/**
 * Whether the app is still running keys older than the ones on this page.
 *
 * `deployStartedAts` is every deploy's creation time. A deploy that STARTED
 * after the change is what picks the new keys up, so a deploy that was
 * already building when the change landed does not count, and a deploy that
 * later failed does not either: nothing shipped, so the old keys are still
 * what is live. Passing `undefined` (deploys not loaded yet) answers false
 * rather than flashing the reminder on every page load before the list
 * arrives.
 */
export function keysAwaitingDeploy(
  changedAt: number | null,
  deployStartedAts: number[] | undefined,
): boolean {
  if (changedAt === null || deployStartedAts === undefined) return false;
  return !deployStartedAts.some((at) => at >= changedAt);
}
